import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { ApiError } from '@lms/api-client';
import type { RegistrationStatus } from '@lms/shared/types';
import { useNotificationStore } from '../store/notifications';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

/**
 * Shape of the backend's 409 "Incomplete User Profile" response, used
 * by the admin UI to auto-open an Edit User dialog prefilled with the
 * fields the backend reported as missing. Mirrors
 * `RegistrationsService.buildIncompleteProfileConflict()`.
 */
export interface IncompleteUserProfileBody {
  statusCode: 409;
  error: 'Incomplete User Profile';
  message: string;
  registrationId: string;
  userId: string;
  missingFields: ('phone' | 'gender')[];
}

export function isIncompleteUserProfileError(
  err: unknown
): err is ApiError & { body: IncompleteUserProfileBody } {
  if (!(err instanceof ApiError)) return false;
  if (err.statusCode !== 409) return false;
  const body = err.body as IncompleteUserProfileBody | undefined;
  return (
    !!body &&
    body.error === 'Incomplete User Profile' &&
    typeof body.userId === 'string' &&
    Array.isArray(body.missingFields)
  );
}

export const registrationKeys = {
  all: ['registrations'] as const,
  lists: () => [...registrationKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...registrationKeys.all, 'list', params] as const,
  details: () => [...registrationKeys.all, 'detail'] as const,
  detail: (id: string) => [...registrationKeys.details(), id] as const,
};

export function useRegistrations(params?: {
  page?: number;
  limit?: number;
  status?: RegistrationStatus;
  programId?: string;
  search?: string;
  /**
   * Set to `true` to restrict the result to registrations that have
   * been re-submitted at least once (i.e. the user re-submitted after
   * their registration was REJECTED / REVISION / CANCELLED). Backed by
   * the `lastResubmittedAt` column.
   */
  resubmitted?: boolean;
}) {
  return useQuery({
    queryKey: registrationKeys.list((params as Record<string, unknown>) || {}),
    queryFn: () => apiClient.registrations.list(params),
  });
}

export function useCreateRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof apiClient.registrations.create>[0]) =>
      apiClient.registrations.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
    },
  });
}

export function useRegistrationDetail(id: string) {
  return useQuery({
    queryKey: registrationKeys.detail(id),
    queryFn: () => apiClient.registrations.get(id),
    enabled: !!id,
  });
}

export function useApproveRegistration() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore(state => state.addNotification);
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, adminNotes }: { id: string; adminNotes?: string }) =>
      apiClient.registrations.approve(id, adminNotes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      addNotification({
        type: 'success',
        title: t(copy.notif.notifApproveSuccessTitle),
        message: t(copy.notif.notifApproveSuccessMsg),
      });
    },
    onError: (error: unknown) => {
      // 409 "Incomplete User Profile" is handled inline by the calling
      // page (which auto-opens an Edit User modal), so we deliberately
      // suppress the global error toast for that case to avoid two
      // surfaces talking at the user at once.
      if (isIncompleteUserProfileError(error)) {
        return;
      }
      addNotification({
        type: 'error',
        title: t(copy.notif.notifApproveErrorTitle),
        message:
          (error instanceof Error ? error.message : undefined) ||
          t(copy.notif.notifApproveErrorMsg),
      });
    },
  });
}

export function useRejectRegistration() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore(state => state.addNotification);
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, adminNotes }: { id: string; adminNotes: string }) =>
      apiClient.registrations.reject(id, adminNotes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      addNotification({
        type: 'success',
        title: t(copy.notif.notifRejectSuccessTitle),
        message: t(copy.notif.notifRejectSuccessMsg),
      });
    },
    onError: (error: { message?: string }) => {
      addNotification({
        type: 'error',
        title: t(copy.notif.notifRejectErrorTitle),
        message: error.message || t(copy.notif.notifRejectErrorMsg),
      });
    },
  });
}

export function useRequestRevision() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore(state => state.addNotification);
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, revisionNotes }: { id: string; revisionNotes: string }) =>
      apiClient.registrations.requestRevision(id, revisionNotes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      addNotification({
        type: 'info',
        title: t(copy.notif.notifRevisionSuccessTitle),
        message: t(copy.notif.notifRevisionSuccessMsg),
      });
    },
    onError: (error: { message?: string }) => {
      addNotification({
        type: 'error',
        title: t(copy.notif.notifRevisionErrorTitle),
        message: error.message || t(copy.notif.notifRevisionErrorMsg),
      });
    },
  });
}

export function useAssignWhatsAppGroup() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore(state => state.addNotification);
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, whatsappGroupId }: { id: string; whatsappGroupId: string }) =>
      apiClient.registrations.assignWhatsAppGroup(id, whatsappGroupId),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      void queryClient.invalidateQueries({ queryKey: registrationKeys.detail(id) });
      addNotification({
        type: 'success',
        title: t(copy.notif.notifAssignSuccessTitle),
        message: t(copy.notif.notifAssignSuccessMsg),
      });
    },
    onError: (error: { message?: string }) => {
      addNotification({
        type: 'error',
        title: t(copy.notif.notifAssignErrorTitle),
        message: error.message || t(copy.notif.notifAssignErrorMsg),
      });
    },
  });
}

export function useReceipt(id: string) {
  return useQuery({
    queryKey: registrationKeys.detail(id),
    queryFn: () => apiClient.registrations.get(id),
    enabled: !!id,
  });
}
